import { FileTextOutlined, UpSquareOutlined } from '@ant-design/icons';
import CenteredLoader from '@client/components/layout/CenteredLoader';
import { useEffect, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';
import { ExtractionsDrawer } from './ExtractionDrawer';
import Styles from './Styles.module.scss';
import ViewerHeader from './ViewerHeader';

interface TxtViewerProps {
    consolidationResult: any;
    loading: boolean;
    highlightKeywords: string[];
    supportingKeywords?: string[];
    contradictoryKeywords?: string[];
    textContent: string | null;
}

export default function TxtViewer({
    consolidationResult,
    loading,
    highlightKeywords = [],
    supportingKeywords = [],
    contradictoryKeywords = [],
    textContent,
}: TxtViewerProps) {
    const [drawerOpen, setDrawerOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const allKeywords = [...highlightKeywords, ...supportingKeywords, ...contradictoryKeywords];

    // Custom highlighting function for multi-color support
    const renderHighlightedText = (text: string) => {
        if (!text) return null;

        // Helper function to create a regex pattern that ignores whitespace differences
        const createFlexiblePattern = (keyword: string): string => {
            if (!keyword || typeof keyword !== 'string') return '';
            // Escape special regex characters first
            const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            // Replace whitespace sequences with flexible whitespace pattern
            // This allows any amount of whitespace (spaces, tabs, newlines) to match
            const pattern = escaped.replace(/\s+/g, '\\s+');
            return pattern;
        };

        // Create an array of all matches with their types
        interface Match {
            start: number;
            end: number;
            text: string;
            type: 'supporting' | 'contradictory' | 'default';
        }

        const matches: Match[] = [];

        // Find supporting evidence matches
        supportingKeywords.forEach((keyword, idx) => {
            if (!keyword || typeof keyword !== 'string') return;
            const pattern = createFlexiblePattern(keyword);
            if (!pattern) return; // Skip if pattern creation failed
            try {
                const regex = new RegExp(pattern, 'gi');
                let match;
                while ((match = regex.exec(text)) !== null) {
                    matches.push({
                        start: match.index,
                        end: match.index + match[0].length,
                        text: match[0],
                        type: 'supporting',
                    });
                }
            } catch (e) {
                // Skip keywords that produce invalid regex patterns
            }
        });

        // Find contradictory evidence matches
        contradictoryKeywords.forEach(keyword => {
            if (!keyword || typeof keyword !== 'string') return;
            const pattern = createFlexiblePattern(keyword);
            if (!pattern) return; // Skip if pattern creation failed
            try {
                const regex = new RegExp(pattern, 'gi');
                let match;
                while ((match = regex.exec(text)) !== null) {
                    matches.push({
                        start: match.index,
                        end: match.index + match[0].length,
                        text: match[0],
                        type: 'contradictory',
                    });
                }
            } catch (e) {
                // Skip keywords that produce invalid regex patterns
            }
        });

        // Sort matches by start position
        matches.sort((a, b) => a.start - b.start);

        // Build the highlighted text
        if (matches.length === 0) {
            return <div>{text}</div>;
        }

        const parts: React.ReactNode[] = [];
        let lastIndex = 0;

        matches.forEach((match, idx) => {
            // Add text before match
            if (match.start > lastIndex) {
                parts.push(<span key={`text-${idx}`}>{text.substring(lastIndex, match.start)}</span>);
            }

            // Add highlighted match
            const bgColor = match.type === 'supporting' ? '#d9f7be' : '#ffe7ba';
            parts.push(
                <mark
                    key={`mark-${idx}`}
                    style={{
                        backgroundColor: bgColor,
                        padding: '2px 0',
                        fontWeight: 500,
                    }}
                >
                    {match.text}
                </mark>
            );

            lastIndex = match.end;
        });

        // Add remaining text
        if (lastIndex < text.length) {
            parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
        }

        return <div>{parts}</div>;
    };

    // Helper function to normalize whitespace for comparison
    const normalizeWhitespace = (text: string): string => {
        return text.replace(/\s+/g, ' ').trim();
    };

    // Scroll to first highlighted keyword after rendering
    useEffect(() => {
        if (!allKeywords.length || !containerRef.current) return;

        // Small delay to ensure DOM is updated with highlights
        const timer = setTimeout(() => {
            if (!containerRef.current) return;

            const firstKeyword = normalizeWhitespace(allKeywords[0].toLowerCase());
            const marks = Array.from(containerRef.current.querySelectorAll<HTMLElement>('mark'));

            const target = marks.find(mark => {
                const markText = normalizeWhitespace((mark.textContent || '').toLowerCase());
                return markText.includes(firstKeyword) || firstKeyword.includes(markText);
            });

            if (target) {
                target.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 100);

        return () => clearTimeout(timer);
    }, [allKeywords, textContent]);

    return (
        <div className={`${Styles.docViewer} ${Styles.viewerTxtWrapper}`}>
            <ViewerHeader />
            <div
                className={Styles.viewerTxtContent}
                ref={containerRef}
            >
                {loading ? (
                    <CenteredLoader loading={loading} />
                ) : (
                    <>
                        <FileTextOutlined className={Styles.fileIcon} />
                        <div className={Styles.highlightedText}>
                            {(supportingKeywords.length > 0 || contradictoryKeywords.length > 0) && textContent ? (
                                renderHighlightedText(textContent)
                            ) : highlightKeywords.length > 0 && textContent ? (
                                <Highlighter
                                    searchWords={highlightKeywords}
                                    autoEscape={true}
                                    textToHighlight={textContent}
                                    highlightTag="mark"
                                />
                            ) : (
                                <div>{textContent}</div>
                            )}
                        </div>
                    </>
                )}
            </div>

            <ExtractionsDrawer
                data={consolidationResult?.content}
                onClose={() => setDrawerOpen(false)}
                open={drawerOpen}
                setDrawerOpen={setDrawerOpen}
            />
            <UpSquareOutlined
                onClick={() => setDrawerOpen(true)}
                className={Styles.extractionsToggle}
            />
        </div>
    );
}
